import React from 'react';
import { CONTACT_EMAILS, EMAIL_SUBJECTS } from '../../constants/contacts';

export type AppCrashPageVariant = 'app' | 'page';

export interface AppCrashPageProps {
  code?: string;
  title: string;
  message: string;
  error?: Error;
  variant?: AppCrashPageVariant;
  showTryAgain?: boolean;
  onTryAgain?: () => void;
  showGoHome?: boolean;
  showSupport?: boolean;
}

function buildSupportMailto(error?: Error): string {
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const body = [
    'A page in the Voluntas app failed to render.',
    '',
    `URL: ${url}`,
    `Time: ${new Date().toISOString()}`,
    ...(error?.message ? [`Error: ${error.message}`] : []),
  ].join('\n');

  return `mailto:${CONTACT_EMAILS.bugs}?subject=${encodeURIComponent(
    EMAIL_SUBJECTS.bugReport
  )}&body=${encodeURIComponent(body)}`;
}

const actionClassName =
  'inline-flex items-center justify-center px-6 py-3 border text-base font-medium rounded-2xl transition-colors duration-200 min-h-[48px]';

const AppCrashPage: React.FC<AppCrashPageProps> = ({
  code = 'Error',
  title,
  message,
  error,
  variant = 'app',
  showTryAgain = false,
  onTryAgain,
  showGoHome = true,
  showSupport = false,
}) => {
  const heightClass = variant === 'page' ? 'min-h-[70vh]' : 'min-h-screen';

  return (
    <div
      className={`${heightClass} bg-neutral-50 flex items-center justify-center p-4`}
      role="alert"
      data-testid="app-crash-page"
    >
      <div className="text-center max-w-md w-full">
        <p className="text-6xl font-bold text-primary-500/30 mb-4">{code}</p>
        <h1 className="text-xl font-semibold text-primary-500 mb-2">{title}</h1>
        <p className="text-primary-500/70 mb-8">{message}</p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {showTryAgain && onTryAgain && (
            <button type="button" onClick={onTryAgain} className={`${actionClassName} border-primary-500 text-primary-500 bg-white hover:bg-primary-500/5`}>
              Try again
            </button>
          )}
          {showGoHome && (
            <a
              href="/"
              className={`${actionClassName} border-transparent text-white bg-primary-500 hover:bg-primary-600`}
            >
              Go home
            </a>
          )}
        </div>

        {showSupport && (
          <p className="text-sm text-primary-500/65 mt-6">
            <a href={buildSupportMailto(error)} className="text-primary-500 hover:underline">
              Email Fieldporter
            </a>
          </p>
        )}

        {process.env.NODE_ENV === 'development' && error && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm font-medium text-primary-500/75 hover:text-primary-500">
              Show error details
            </summary>
            <pre className="mt-2 p-3 bg-black/5 rounded text-xs font-mono text-primary-500 overflow-auto max-h-32 whitespace-pre-wrap">
              {error.stack || error.message}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

export default AppCrashPage;
